// kernel/designer/jnn/useJnnDesigner.ts
import { useCallback, RefObject } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { AgentMode, DesignerAttachment, DesignerItem, DesignerTurnEvent, DesignerTurnRequestBody, ErrorPayload, KernelHandle, LockableFile, Message } from '#types/types';
import { fetchApi } from '@/kernel/apiFetch';
import { DESIGNER_COMPACT_FAILED, collectDesignerItems, isDesignerImageRef, isEndedDesignerTurn, readDesignerCompactCommand } from '#lib/designer/jnn/items';
import {
  emitAssistantMessageEventFromSession,
  patchSessionRuntimeIfAbortControllerOwner,
  resolveMessageTargetFromSession,
  takeQueuedMessage,
} from '@/kernel/agentTurnShared';
import { designerModelView, readDesignerRestrictions, readDesignerStudioState, runDesignerBrowserCalls } from '@/kernel/designer/jnn/browserCalls';
import { DESIGNER_BILLED_TOOLS, DESIGNER_JNN_ACTIONS, DESIGNER_GENERATE_TOOL, DESIGNER_TURN_HEARTBEAT_S } from '#lib/designer/jnn/browserTools';
import { designerBudgetCredits } from '#lib/designer/jnn/budget';
import { uploadFileToCloud } from '#lib/azure-client';
import { getFileExtension } from '#lib/utils';
import { listPromptUploadOwner } from '#lib/list/listPromptUploadOwner';

type TurnRequestTail<B = DesignerTurnRequestBody> = B extends unknown ? Omit<B, 'brain' | 'sessionId' | 'projectId'> : never;

const TURN_FAILED = 'The assistant could not complete this request. Please try again.';
const UPLOAD_FAILED = 'Your attached image could not be uploaded, so nothing was sent. Your message and image are back in the box: send them again.';
const FOREIGN_IMAGE = 'This Designer only reads images attached in this chat. Your message is back in the box.';
const COMPACTING = 'Compacting conversation';

const describedAttachment = (ref: string, name: string, { view, size }: Awaited<ReturnType<typeof designerModelView>>): DesignerAttachment =>
  ({ ref, view: view ?? ref, name, ...(size ?? {}) });

/**
 * The message's attachments: each file uploaded as the legacy turn uploads it (same helper, owner and naming), each image
 * already stored read back, both beside their model view; null when a file's upload failed.
 */
const uploadDesignerAttachments = async (
  kernel: RefObject<KernelHandle | null>, projectId: string, images: LockableFile[], imageUrls: string[],
): Promise<DesignerAttachment[] | null> => {
  const owner = listPromptUploadOwner(kernel.current?.getInventory().Project.registry[projectId], kernel.current?.getUserContext().dbUserId);
  const storeView = (view: Blob) => uploadFileToCloud(view, `designer_view_${uuidv4()}.webp`, 'image/webp', undefined, owner);
  const attachments = await Promise.all([
    ...images.map(async ({ file }) => {
      const filename = `${Date.now()}${Math.random().toString(36).substring(2, 4)}.${getFileExtension(file.name) || 'bin'}`;
      const [ref, model] = await Promise.all([uploadFileToCloud(file, filename, file.type, undefined, owner).catch(() => null), designerModelView(file, storeView)]);
      return ref ? describedAttachment(ref, file.name, model) : null;
    }),
    ...imageUrls.map(async ref => {
      const stored = await fetch(ref).then(response => (response.ok ? response.blob() : null), () => null);
      return describedAttachment(ref, ref.split('/').pop() ?? '', stored ? await designerModelView(stored, storeView) : { view: null, size: null });
    }),
  ]);
  if (attachments.some(attachment => !attachment)) return null;
  const described = attachments as DesignerAttachment[];
  const registered = [...images.map((_, at) => described[at]!.ref), ...described.flatMap(attachment => (attachment.view === attachment.ref ? [] : [attachment.view]))];
  if (registered.length) kernel.current?.registerCloudAssets(registered, 'upload');
  return described;
};

/**
 * /compact: the conversation so far becomes one compaction item, kept on an empty Designer message the chat draws
 * as a divider; every later turn reads the conversation from it. The command itself is not a message.
 */
const compactJnnConversation = async (kernel: RefObject<KernelHandle | null>, input: {
  sessionId: string; projectId: string; priorItems: DesignerItem[]; focus?: string; prompt: string;
}): Promise<void> => {
  const { sessionId, projectId, priorItems, focus } = input;
  if (!kernel.current) return;
  // Held like a turn, so the composer waits and Stop cancels it.
  const abortController = new AbortController();
  kernel.current.patchEntityRuntimeState('ChatSession', sessionId, { abortController, isStopRequested: false });
  // Images attached beside the command stay in the box for the next message.
  kernel.current.setChatPrompt('');
  kernel.current.setSessionStatus(sessionId, COMPACTING);
  let message: Message | null = null;
  let refusal: string | null = null;
  try {
    const response = await fetchApi('/api/ai/designer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ brain: 'jnn', kind: 'compact', sessionId, projectId, priorItems, ...(focus ? { focus } : {}) }),
      signal: abortController.signal,
    });
    const answer = await response.json().catch(() => null) as { item?: DesignerItem; message?: unknown } | null;
    if (response.ok && answer?.item) {
      message = {
        content: '', role: 'assistant', timestamp: new Date().toISOString(), agentName: 'Fater', designerItems: [answer.item],
        ...resolveMessageTargetFromSession(kernel.current?.getInventory().ChatSession.registry[sessionId]),
      };
    } else refusal = typeof answer?.message === 'string' ? answer.message : DESIGNER_COMPACT_FAILED;
  } catch (error) {
    if (!abortController.signal.aborted) console.error('[useJnnDesigner] Designer compaction failed', error);
    refusal = DESIGNER_COMPACT_FAILED;
  } finally {
    kernel.current?.setSessionStatus(sessionId, '');
    patchSessionRuntimeIfAbortControllerOwner(kernel.current, sessionId, abortController, { abortController: null });
  }
  if (!message) {
    kernel.current?.setChatPrompt(input.prompt);
    if (!abortController.signal.aborted) kernel.current?.handleError(refusal ?? DESIGNER_COMPACT_FAILED);
    return;
  }
  const compacted = message;
  kernel.current?.updateActiveSessionMessages(previous => [...previous, compacted], sessionId);
  void emitAssistantMessageEventFromSession(kernel.current, sessionId, compacted.timestamp);
};

interface UseJnnDesignerProps {
  kernel: RefObject<KernelHandle | null>;
  getProjectIdFromSessionId: (sessionId: string) => string | null;
}

/** The jnn brain's turn, with the legacy executor's call shape so useAgents dispatches either. */
export function useJnnDesigner({ kernel, getProjectIdFromSessionId }: UseJnnDesignerProps) {
  const executeJnnTurn = useCallback(async (
    sessionId: string,
    workspaceId: string,
    prompt: string,
    images: LockableFile[],
    isUserTurn: boolean,
    imageUrls: string[] = [],
    _agentMode?: AgentMode,
    _onScriptComplete?: (finalWorkspaceId: string) => Promise<void>,
    _lastScriptError?: ErrorPayload,
    _progressiveStepRetryCount?: number,
    skipUserMessage: boolean = false,
  ): Promise<void> => {
    if (!kernel.current) return;
    // Its actions run as single-execution scripts, which wake no assistant, and an action only ever runs in a turn the user typed;
    // a recall is the legacy turn's own (useAgents' triggerReEvaluation), never dispatched here.
    if (!isUserTurn) {
      kernel.current.handleError('This Designer only answers your messages for now.');
      return;
    }
    const projectId = getProjectIdFromSessionId(sessionId);
    if (!kernel.current.getInventory().ChatSession.registry[sessionId] || !projectId) {
      kernel.current.handleError(TURN_FAILED);
      return;
    }
    takeQueuedMessage(kernel.current, sessionId);
    if (kernel.current.readEntityRuntimeState('ChatSession', sessionId).isStopRequested) return;

    const priorItems = collectDesignerItems(kernel.current.getChatSessionMessages(sessionId));
    const compact = readDesignerCompactCommand(prompt);
    if (compact) {
      await compactJnnConversation(kernel, { sessionId, projectId, priorItems, focus: compact.focus, prompt });
      return;
    }
    const abortController = new AbortController();
    kernel.current.patchEntityRuntimeState('ChatSession', sessionId, { abortController, isStopRequested: false, designerViews: {}, designerTray: {}, designerSteps: { made: [], undone: [] } });
    kernel.current.startThinkingSequence?.(sessionId);
    kernel.current.setChatPrompt('');
    kernel.current.setImagesInChatInput([]);

    let messageTimestamp: string | null = null;
    let completed = false;
    const patchAssistantMessage = (timestamp: string, patch: (message: Message) => Message) =>
      kernel.current?.updateActiveSessionMessages(previous => previous.map(message =>
        message.timestamp === timestamp && message.role === 'assistant' ? patch(message) : message), sessionId);
    const appendText = (text: string): string => {
      if (messageTimestamp) {
        patchAssistantMessage(messageTimestamp, message => ({ ...message, content: message.content + text }));
        return messageTimestamp;
      }
      const timestamp = new Date().toISOString();
      messageTimestamp = timestamp;
      const message: Message = {
        content: text,
        role: 'assistant',
        timestamp,
        agentName: 'Fater',
        ...resolveMessageTargetFromSession(kernel.current?.getInventory().ChatSession.registry[sessionId]),
      };
      kernel.current?.updateActiveSessionMessages(previous => [...previous, message], sessionId);
      return timestamp;
    };
    const completeTurn = (items: DesignerItem[]) => {
      patchAssistantMessage(appendText(''), message => ({ ...message, designerItems: items }));
      completed = true;
    };

    // A turn that waits on the browser goes on in further requests of the same turn: the server
    // hands its items and calls over, the browser answers each call and sends them back.
    const request = (extra: TurnRequestTail) =>
      fetchApi('/api/ai/designer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brain: 'jnn', sessionId, projectId, ...extra }),
        // A stop outlives the aborted turn, and the tab when it closes.
        ...(extra.kind === 'stop' ? { keepalive: true } : extra.kind === 'touch' ? {} : { signal: abortController.signal }),
      });
    let turnId: string | null = null;
    // Read when the message is sent: the chip's value then belongs to this turn alone.
    const budgetCredits = designerBudgetCredits(kernel.current.getState().designerBudget);
    let sent = false;
    const restoreComposer = () => {
      kernel.current?.setChatPrompt(prompt);
      kernel.current?.setImagesInChatInput(images);
    };
    try {
      // A message about an image never runs without it: the model would invent one instead.
      // A foreign address is refused before it is ever fetched.
      const foreignUrl = !imageUrls.every(isDesignerImageRef);
      const attachments = foreignUrl ? [] : await uploadDesignerAttachments(kernel, projectId, images, imageUrls);
      const foreign = foreignUrl || Boolean(attachments?.some(({ ref, view }) => !isDesignerImageRef(ref) || !isDesignerImageRef(view)));
      if (!attachments || foreign) {
        restoreComposer();
        kernel.current?.handleError(foreign ? FOREIGN_IMAGE : UPLOAD_FAILED);
        return;
      }
      abortController.signal.throwIfAborted();
      const imageRefs = attachments.map(attachment => attachment.ref);
      if (!skipUserMessage) kernel.current?.addUserMessage(prompt, imageRefs.length > 0 ? imageRefs : undefined);
      sent = true;
      // The project history the agent reads is told on the user's own clock.
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let response = await request({
        kind: 'start', prompt, priorItems, restrictions: readDesignerRestrictions(kernel, projectId), timeZone, ...(attachments.length > 0 ? { attachments } : {}),
        ...(budgetCredits === undefined ? {} : { budgetCredits, studio: readDesignerStudioState(kernel, projectId) }),
      });
      for (;;) {
        let waiting: Extract<DesignerTurnEvent, { type: 'browserCalls' }>['payload'] | null = null;
        if (response.status === 403) {
          kernel.current?.handleNotEnoughCreditsError();
        } else if (!response.ok || !response.body) {
          const refusal = await response.json().catch(() => null) as { message?: unknown } | null;
          if (!isEndedDesignerTurn(response.status, refusal)) kernel.current?.handleError(typeof refusal?.message === 'string' ? refusal.message : TURN_FAILED);
        } else {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const frames = buffer.split('\n\n');
            buffer = frames.pop() ?? '';
            for (const frame of frames) {
              if (!frame.startsWith('data: ')) continue;
              const event = JSON.parse(frame.slice(6)) as DesignerTurnEvent;
              if (event.type === 'textDelta') appendText(event.payload);
              else if (event.type === 'textRetract' && messageTimestamp) patchAssistantMessage(messageTimestamp, message => ({ ...message, content: message.content.slice(0, -event.payload.chars) }));
              else if (event.type === 'turnComplete') completeTurn(event.payload.items);
              else if (event.type === 'browserCalls') waiting = event.payload;
              else if (event.type === 'error') kernel.current?.handleError(event.payload.message);
              else if (event.type === 'compacting') kernel.current?.setSessionStatus(sessionId, COMPACTING);
              else if (event.type === 'compacted') {
                kernel.current?.setSessionStatus(sessionId, '');
                kernel.current?.startThinkingSequence?.(sessionId);
              }
            }
          }
        }
        if (!waiting) break;
        turnId = waiting.turnId;
        if (kernel.current?.readEntityRuntimeState('ChatSession', sessionId).isStopRequested) abortController.abort();
        abortController.signal.throwIfAborted();
        const calls = waiting.calls;
        kernel.current?.setSessionStatus(sessionId, calls.some(call => call.name === DESIGNER_GENERATE_TOOL) ? 'Generating'
          : calls.some(call => DESIGNER_JNN_ACTIONS[call.name] || DESIGNER_BILLED_TOOLS.has(call.name)) ? 'Working on the canvas'
            : calls.some(call => call.name === 'view_area') ? 'Looking at the canvas'
              : calls.some(call => call.name === 'view_image' || call.name === 'crop_image') ? 'Looking at an image' : 'Reading the canvas');
        // A generation outlasts the sweep's idle limit: touch the turn while the calls run, the last touch landing before the continuation.
        const waitingTurnId = turnId;
        let lastTouch: Promise<unknown> = Promise.resolve();
        // A refused touch (the turn ended, or is past its maximum age) is final: stop touching.
        const heartbeat = setInterval(() => {
          lastTouch = request({ kind: 'touch', turnId: waitingTurnId })
            .then(response => { if (response.status === 409) clearInterval(heartbeat); })
            .catch(() => undefined);
        }, DESIGNER_TURN_HEARTBEAT_S * 1000);
        let callOutputs: DesignerItem[];
        try {
          callOutputs = await runDesignerBrowserCalls(kernel, calls, { projectId, workspaceId, sessionId, controller: abortController });
        } finally {
          clearInterval(heartbeat);
          await lastTouch;
        }
        abortController.signal.throwIfAborted();
        kernel.current?.startThinkingSequence?.(sessionId);
        response = await request({
          // A turn that started by compacting opens on the item standing for every earlier turn: they are not sent again.
          kind: 'continue', turnId, priorItems: waiting.items.some(item => item.type === 'compaction') ? [] : priorItems, turnItems: waiting.items, callOutputs, restrictions: readDesignerRestrictions(kernel, projectId), timeZone,
          ...(waiting.budgeted ? { studio: readDesignerStudioState(kernel, projectId) } : {}),
        });
      }
    } catch (error) {
      // Stopped during the upload: the message never left, so it goes back in the box.
      if (!sent) restoreComposer();
      if (!abortController.signal.aborted) {
        console.error('[useJnnDesigner] Designer turn failed', error);
        kernel.current?.handleError(TURN_FAILED);
      }
    } finally {
      kernel.current?.setSessionStatus(sessionId, '');
      patchSessionRuntimeIfAbortControllerOwner(kernel.current, sessionId, abortController, { abortController: null });
    }
    // Stopped: the server settles a turn waiting on the browser now, and flags one a request still runs for that request to settle.
    if (abortController.signal.aborted && turnId) void request({ kind: 'stop', turnId }).catch(() => undefined);

    if (!completed || !messageTimestamp || !kernel.current) return;
    void emitAssistantMessageEventFromSession(kernel.current, sessionId, messageTimestamp);
    void kernel.current.maybeAutoTitleSession?.(sessionId);
  }, [kernel, getProjectIdFromSessionId]);

  return { executeJnnTurn };
}
